import React, { lazy, Suspense, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation, useParams, useSearchParams } from 'react-router-dom';
import './styles.css';
import { Layout } from './components/Layout';
import { ErrorBoundary } from './components/ErrorBoundary';
import { notifyAuthTokenChanged, notifyAuthUserChanged } from './constants/auth-events';
import { STORAGE_KEYS } from './constants/storage-keys';
import { authApi, hasPermission, getStoredRoleName, getUserPermissions } from './api/auth';
import { FEATURE_REPORTS_ENABLED, FEATURE_WAREHOUSE_ENABLED } from './config/features';
import { resolveHomePath } from './config/navigation';
import { PermissionRoute } from './components/PermissionRoute';
import { RepProfileGate } from './components/RepProfileGate';

const DashboardPage = lazy(() => import('./features/infra/DashboardPage').then(m => ({ default: m.DashboardPage })));
const ClientsPage = lazy(() => import('./features/crm/ClientsPage').then(m => ({ default: m.ClientsPage })));
const RegionalRepresentativesPage = lazy(() => import('./features/crm/RegionalRepresentativesPage').then(m => ({ default: m.RegionalRepresentativesPage })));
const CitiesPage = lazy(() => import('./features/reference/CitiesPage').then(m => ({ default: m.CitiesPage })));
const DriversPage = lazy(() => import('./features/reference/DriversPage').then(m => ({ default: m.DriversPage })));
const TransportsPage = lazy(() => import('./features/reference/TransportsPage').then(m => ({ default: m.TransportsPage })));
const BranchesPage = lazy(() => import('./features/reference/BranchesPage').then(m => ({ default: m.BranchesPage })));
const RouteDirectoryPage = lazy(() => import('./features/reference/RouteDirectoryPage').then(m => ({ default: m.RouteDirectoryPage })));
const TarifsPage = lazy(() => import('./features/finance/TarifsPage').then(m => ({ default: m.TarifsPage })));
// RepTarifsPage — архив, тариф теперь задаётся inline в форме Исполнителя
const AdditionalServicesPage = lazy(() => import('./features/reference/AdditionalServicesPage').then(m => ({ default: m.AdditionalServicesPage })));
const RoundExtraServicesPage = lazy(() => import('./features/reference/RoundExtraServicesPage').then(m => ({ default: m.RoundExtraServicesPage })));
const LoginPage = lazy(() => import('./features/iam/LoginPage').then(m => ({ default: m.LoginPage })));
const UsersPage = lazy(() => import('./features/iam/UsersPage'));
const RolesPage = lazy(() => import('./features/iam/RolesPage'));
const ReportsPage = lazy(() => import('./features/infra/ReportsPage').then(m => ({ default: m.ReportsPage })));
const DirectionsPage = lazy(() => import('./features/logistics/DirectionsPage').then(m => ({ default: m.DirectionsPage })));
const DeliveryTimesPage = lazy(() => import('./features/logistics/DeliveryTimesPage').then(m => ({ default: m.DeliveryTimesPage })));
const AuditLogPage = lazy(() => import('./features/iam/AuditLogPage').then(m => ({ default: m.AuditLogPage })));
const FirmsPage = lazy(() => import('./features/iam/FirmsPage').then(m => ({ default: m.FirmsPage })));
const IntegrationsPage = lazy(() => import('./features/iam/IntegrationsPage').then(m => ({ default: m.IntegrationsPage })));
const CouriersPage = lazy(() => import('./features/reference/CouriersPage').then(m => ({ default: m.CouriersPage })));
const TransportTypesPage = lazy(() => import('./features/reference/TransportTypesPage').then(m => ({ default: m.TransportTypesPage })));
const BanksPage = lazy(() => import('./features/finance/BanksPage').then(m => ({ default: m.BanksPage })));
const CostTypesPage = lazy(() => import('./features/finance/CostTypesPage').then(m => ({ default: m.CostTypesPage })));
const CarriersPage = lazy(() => import('./features/reference/CarriersPage').then(m => ({ default: m.CarriersPage })));
const BoxTypesPage = lazy(() => import('./features/reference/BoxTypesPage').then(m => ({ default: m.BoxTypesPage })));
const ContactFacesPage = lazy(() => import('./features/crm/ContactFacesPage').then(m => ({ default: m.ContactFacesPage })));
const IncidentsPage = lazy(() => import('./features/quality/IncidentsPage').then(m => ({ default: m.IncidentsPage })));
const PrintOrderPage = lazy(() => import('./features/orders/PrintOrderPage').then(m => ({ default: m.PrintOrderPage })));
const PrintCityOrderPage = lazy(() => import('./features/orders/PrintCityOrderPage').then(m => ({ default: m.PrintCityOrderPage })));
const RoundPrintPackPage = lazy(() => import('./features/logistics/RoundPrintPackPage').then(m => ({ default: m.RoundPrintPackPage })));
const RoundReconciliationPage = lazy(() => import('./features/logistics/RoundReconciliationPage').then(m => ({ default: m.RoundReconciliationPage })));
const ClientManualTtnPage = lazy(() => import('./features/client-portal/ClientManualTtnPage').then(m => ({ default: m.ClientManualTtnPage })));
const ClientOrdersPage = lazy(() => import('./features/client-portal/ClientOrdersPage').then(m => ({ default: m.ClientOrdersPage })));
const ClientPreorderPage = lazy(() => import('./features/client-portal/ClientPreorderPage').then(m => ({ default: m.ClientPreorderPage })));
const ClientAttachTtnPage = lazy(() => import('./features/client-portal/ClientAttachTtnPage').then(m => ({ default: m.ClientAttachTtnPage })));
const ClientOrderCardPage = lazy(() => import('./features/client-portal/ClientOrderCardPage').then(m => ({ default: m.ClientOrderCardPage })));
const ClientBranchAccountsPage = lazy(() => import('./features/client-portal/ClientBranchAccountsPage').then(m => ({ default: m.ClientBranchAccountsPage })));
const TtnDeviationsPage = lazy(() => import('./features/orders/TtnDeviationsPage').then(m => ({ default: m.TtnDeviationsPage })));
const ResolutionsPage = lazy(() => import('./features/quality/ResolutionsPage').then(m => ({ default: m.ResolutionsPage })));
const ExchangeRatesPage = lazy(() => import('./features/finance/ExchangeRatesPage').then(m => ({ default: m.ExchangeRatesPage })));
const PersonalsPage = lazy(() => import('./features/iam/PersonalsPage').then(m => ({ default: m.PersonalsPage })));
const MessagesPage = lazy(() => import('./features/crm/MessagesPage').then(m => ({ default: m.MessagesPage })));
// RatesPage removed from navigation (FR-031) — backend rate module preserved
const AcceptanceReportsPage = lazy(() => import('./features/logistics/AcceptanceReportsPage').then(m => ({ default: m.AcceptanceReportsPage })));
const WarehouseStockPage = lazy(() => import('./features/warehouse/WarehouseStockPage'));
const WarehouseCityRoundsPage = lazy(() => import('./features/warehouse/WarehouseCityRoundsPage'));
const WarehouseRegionalRoundsPage = lazy(() => import('./features/warehouse/WarehouseRegionalRoundsPage'));
const WarehouseRoundsPage = lazy(() => import('./features/warehouse/WarehouseRoundsPage'));
const WarehouseLoadingPage = lazy(() => import('./features/warehouse/WarehouseLoadingPage'));
const WarehouseReceivePage = lazy(() => import('./features/warehouse/WarehouseReceivePage'));
const WarehouseReceiveRoundPage = lazy(() => import('./features/warehouse/WarehouseReceiveRoundPage'));
const WarehouseArchivePage = lazy(() => import('./features/warehouse/WarehouseArchivePage'));
const WarehouseSearchPage = lazy(() => import('./features/warehouse/WarehouseSearchPage'));
const WarehouseJournalPage = lazy(() => import('./features/warehouse/WarehouseJournalPage'));
const AllOrdersPage = lazy(() => import('./features/orders/AllOrdersPage').then(m => ({ default: m.AllOrdersPage })));
const AllRoundsPageV2 = lazy(() => import('./features/logistics/AllRoundsPageV2').then(m => ({ default: m.AllRoundsPageV2 })));
const RoundFormPageV2 = lazy(() => import('./features/logistics/RoundFormPageV2').then(m => ({ default: m.RoundFormPageV2 })));
const RepStockPage = lazy(() => import('./features/logistics/RepStockPage').then(m => ({ default: m.RepStockPage })));
const RepPickupsPage = lazy(() => import('./features/logistics/RepPickupsPage').then(m => ({ default: m.RepPickupsPage })));
const XdOutboundPoolPage = lazy(() => import('./features/logistics/XdOutboundPoolPage').then(m => ({ default: m.XdOutboundPoolPage })));
const RepLinehaulPage = lazy(() => import('./features/logistics/RepLinehaulPage').then(m => ({ default: m.RepLinehaulPage })));
const RepPodPage = lazy(() => import('./features/logistics/RepPodPage').then(m => ({ default: m.RepPodPage })));
const RpRoundsPage = lazy(() => import('./features/logistics/RpRoundsPage').then(m => ({ default: m.RpRoundsPage })));
const AdminRpRoundsPage = lazy(() => import('./features/logistics/AdminRpRoundsPage').then(m => ({ default: m.AdminRpRoundsPage })));
const RepDashboardPage = lazy(() => import('./features/logistics/RepDashboardPage').then(m => ({ default: m.RepDashboardPage })));
const RepOpsPage = lazy(() => import('./features/logistics/RepOpsPage').then(m => ({ default: m.RepOpsPage })));
const RepMySettlementsPage = lazy(() => import('./features/logistics/RepMySettlementsPage').then(m => ({ default: m.RepMySettlementsPage })));
const RepDriversPage = lazy(() => import('./features/logistics/RepDriversPage').then(m => ({ default: m.RepDriversPage })));
const RepRegistryPage = lazy(() => import('./features/crm/RepRegistryPage').then(m => ({ default: m.RepRegistryPage })));
const MarginReportPage = lazy(() => import('./features/finance/MarginReportPage').then(m => ({ default: m.MarginReportPage })));
const TrackingPage = lazy(() => import('./features/orders/TrackingPage').then(m => ({ default: m.TrackingPage })));
const PublicTrackPage = lazy(() => import('./features/orders/PublicTrackPage').then(m => ({ default: m.PublicTrackPage })));
const FinancePage = lazy(() => import('./features/finance/FinancePage').then(m => ({ default: m.FinancePage })));
const ReceivablesPage = lazy(() => import('./features/finance/ReceivablesPage').then(m => ({ default: m.ReceivablesPage })));
const DeliveryRegistryPage = lazy(() => import('./features/finance/DeliveryRegistryPage').then(m => ({ default: m.DeliveryRegistryPage })));
const CargoLocationPage = lazy(() => import('./features/logistics/CargoLocationPage').then(m => ({ default: m.CargoLocationPage })));
const ExceptionsPage = lazy(() => import('./features/logistics/ExceptionsPage').then(m => ({ default: m.ExceptionsPage })));
const RepSettlementsPage = lazy(() => import('./features/finance/RepSettlementsPage').then(m => ({ default: m.RepSettlementsPage })));
const MarginLeaksPage = lazy(() => import('./features/finance/MarginLeaksPage').then(m => ({ default: m.MarginLeaksPage })));
const UnifiedOrderFormPage = lazy(() => import('./features/orders/unified/UnifiedOrderFormPage').then(m => ({ default: m.UnifiedOrderFormPage })));
const EmailQueuePage = lazy(() => import('./features/infra/EmailQueuePage').then(m => ({ default: m.EmailQueuePage })));
const TesterFeedbackPage = lazy(() => import('./features/infra/TesterFeedbackPage').then(m => ({ default: m.TesterFeedbackPage })));
const DocumentPackPage = lazy(() => import('./features/logistics/DocumentPackPage').then(m => ({ default: m.DocumentPackPage })));
const SecurityPage = lazy(() => import('./features/iam/SecurityPage').then(m => ({ default: m.SecurityPage })));


function PageSpinner() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <div style={{ fontSize: 14, color: '#666' }}>Загрузка...</div>
    </div>
  );
}

/** Redirects unauthenticated users to /login, preserving the intended URL */
function ProtectedRoute() {
  const location = useLocation();
  const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
  if (!token) {
    return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />;
  }
  return <Outlet />;
}

/** Redirect /orders/new?type=X → /unified-orders/new?type=X (preserves type param) */
function NewOrderRedirect() {
  const [params] = useSearchParams();
  const type = params.get('type') ?? 'regional';
  const cloneFrom = params.get('cloneFrom');
  const qs = new URLSearchParams({ type });
  if (cloneFrom) qs.set('cloneFrom', cloneFrom);
  return <Navigate to={`/unified-orders/new?${qs.toString()}`} replace />;
}

function CityNewOrderRedirect() {
  const [params] = useSearchParams();
  const cloneFrom = params.get('cloneFrom');
  const qs = new URLSearchParams({ type: 'city' });
  if (cloneFrom) qs.set('cloneFrom', cloneFrom);
  return <Navigate to={`/unified-orders/new?${qs.toString()}`} replace />;
}

/** Redirect /orders/:id/edit → /unified-orders/:id/edit?type=regional */
function RegionalOrderEditRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/unified-orders/${id}/edit?type=regional`} replace />;
}

/** Redirect /city-orders/:id/edit → /unified-orders/:id/edit?type=city */
function CityOrderEditRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/unified-orders/${id}/edit?type=city`} replace />;
}

/** Redirect /city-rounds/:id/edit → /rounds/:id/edit */
function CityRoundEditRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/rounds/${id}/edit?type=city`} replace />;
}

/** Redirect /city-rounds/new → /rounds/new?type=city (preserve orderIds etc. + state) */
function CityRoundsNewRedirect() {
  const [params] = useSearchParams();
  const location = useLocation();
  const qs = new URLSearchParams(params);
  qs.set('type', 'city');
  return <Navigate to={`/rounds/new?${qs.toString()}`} replace state={location.state} />;
}

function RoundsRedirect() {
  const [params] = useSearchParams();
  const type = params.get('type') ?? 'regional';
  return <Navigate to={`/all-rounds?type=${type}`} replace />;
}

function HomeRoute() {
  const dest = resolveHomePath(getUserPermissions(), hasPermission, getStoredRoleName());
  if (dest === '/') return <DashboardPage />;
  return <Navigate to={dest} replace />;
}

function App() {
  // null = has token, validating; true/false = determined
  const [isAuth, setIsAuth] = useState<boolean | null>(() =>
    localStorage.getItem(STORAGE_KEYS.TOKEN) ? null : false
  );

  useEffect(() => {
    if (isAuth !== null) return; // already determined (false = no token)
    const bootstrap = async () => {
      try {
        const user = await authApi.me();
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
        notifyAuthUserChanged();
        setIsAuth(true);
      } catch {
        try {
          const res = await authApi.refresh();
          localStorage.setItem(STORAGE_KEYS.TOKEN, res.access_token);
          localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(res.user));
          notifyAuthUserChanged();
          notifyAuthTokenChanged();
          setIsAuth(true);
        } catch {
          localStorage.removeItem(STORAGE_KEYS.TOKEN);
          localStorage.removeItem(STORAGE_KEYS.USER);
          setIsAuth(false);
        }
      }
    };
    bootstrap();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onStorage = () => {
      const hasToken = !!localStorage.getItem(STORAGE_KEYS.TOKEN);
      setIsAuth(prev => (prev === null ? prev : hasToken));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Global UX fix: select all text when focusing a number input so the default
  // "0" doesn't block typing — user can immediately enter the desired value.
  useEffect(() => {
    const onFocusIn = (e: FocusEvent) => {
      const el = e.target as HTMLInputElement;
      if (el.tagName === 'INPUT' && el.type === 'number') {
        try { el.select(); } catch { /* number inputs don't support select() in Chrome */ }
      }
    };
    document.addEventListener('focusin', onFocusIn);
    return () => document.removeEventListener('focusin', onFocusIn);
  }, []);

  const handleLogin = () => setIsAuth(true);
  const handleLogout = () => {
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER);
    setIsAuth(false);
  };

  // Validating token — show spinner
  if (isAuth === null) {
    return <PageSpinner />;
  }

  return (
    <BrowserRouter>
      <ErrorBoundary>
        <Suspense fallback={<PageSpinner />}>
          <Routes>
            {/* Public route — if already logged in, redirect to root */}
            <Route
              path="/login"
              element={isAuth ? <Navigate to="/" replace /> : <LoginPage onLogin={handleLogin} />}
            />
            <Route path="/t/:invoice" element={<PublicTrackPage />} />
            {/* Protected routes */}
            <Route element={<ProtectedRoute />}>
              <Route element={<Layout onLogout={handleLogout} />}>
                <Route path="/" element={<HomeRoute />} />
                {/* Self-service security (MFA) — any authenticated user */}
                <Route path="/security" element={<SecurityPage />} />

                {/* Orders */}
                <Route element={<PermissionRoute anyOf={['orders.view', 'city-orders.view', 'rounds.view']} />}>
                  <Route path="/exceptions" element={<ExceptionsPage />} />
                </Route>
                <Route element={<PermissionRoute anyOf={['orders.view', 'city-orders.view']} />}>
                  <Route path="/pipeline" element={<Navigate to="/all-orders" replace />} />
                  <Route path="/all-orders" element={<AllOrdersPage />} />
                  <Route path="/cargo-location" element={<CargoLocationPage />} />
                  <Route path="/unified-orders/new" element={<UnifiedOrderFormPage />} />
                  <Route path="/unified-orders/:id/edit" element={<UnifiedOrderFormPage />} />
                  <Route path="/ttn-deviations" element={<TtnDeviationsPage />} />
                  <Route path="/incidents" element={<IncidentsPage />} />
                  <Route path="/orders/:id/print" element={<PrintOrderPage />} />
                  <Route path="/city-orders/:id/print" element={<PrintCityOrderPage />} />
                </Route>

                {/* Rounds */}
                <Route element={<PermissionRoute anyOf={['rounds.view', 'city-rounds.view', 'rounds.scan']} />}>
                  <Route path="/all-rounds" element={<AllRoundsPageV2 />} />
                  <Route path="/rounds/:id/edit" element={<RoundFormPageV2 />} />
                  <Route path="/rounds/:id/reconciliation" element={<RoundReconciliationPage />} />
                  <Route path="/rounds/:id/print-pack" element={<RoundPrintPackPage />} />
                  <Route path="/rounds/new" element={<RoundFormPageV2 />} />
                </Route>

                {/* РП portal — soft empty state if no linked RegionalRepresentative */}
                <Route element={<PermissionRoute permission="rep.rounds.view" />}>
                  <Route path="/representative/dashboard" element={<RepProfileGate><RepDashboardPage /></RepProfileGate>} />
                  <Route path="/rep/rounds" element={<Navigate to="/rep/linehaul" replace />} />
                  <Route path="/rep/stock" element={<RepProfileGate><RepStockPage /></RepProfileGate>} />
                  <Route path="/rep/xd-outbound" element={<RepProfileGate><XdOutboundPoolPage /></RepProfileGate>} />
                  <Route path="/rep/linehaul/:roundId" element={<RepProfileGate><RepLinehaulPage /></RepProfileGate>} />
                  <Route path="/rep/linehaul" element={<RepProfileGate><RepLinehaulPage /></RepProfileGate>} />
                  <Route path="/rep/ops" element={<RepProfileGate><RepOpsPage /></RepProfileGate>} />
                  <Route path="/rep/settlements" element={<RepProfileGate><RepMySettlementsPage /></RepProfileGate>} />
                </Route>
                <Route element={<PermissionRoute permission="rep.rounds.view" />}>
                  <Route path="/rep/orders/new" element={<Navigate to="/representative/dashboard" replace />} />
                </Route>
                <Route element={<PermissionRoute anyOf={['client.ttn.view', 'client.ttn.create']} />}>
                  <Route path="/client/orders" element={<ClientOrdersPage />} />
                  <Route path="/client/orders/:id" element={<ClientOrderCardPage />} />
                </Route>
                <Route element={<PermissionRoute permission="client.ttn.create" />}>
                  <Route path="/client/orders/new" element={<ClientPreorderPage />} />
                  <Route path="/client/accounts" element={<ClientBranchAccountsPage />} />
                  <Route path="/client/orders/:id/ttn" element={<ClientAttachTtnPage />} />
                  <Route path="/client/ttn/new" element={<ClientManualTtnPage />} />
                </Route>
                <Route element={<PermissionRoute permission="rep.pod.create" />}>
                  <Route path="/rep/pod" element={<RepProfileGate><RepPodPage /></RepProfileGate>} />
                </Route>
                <Route element={<PermissionRoute permission="rep.rp-rounds.view" />}>
                  <Route path="/rep/rp-rounds" element={<RepProfileGate><RpRoundsPage /></RepProfileGate>} />
                  <Route path="/rep/pickups" element={<RepProfileGate><RepPickupsPage /></RepProfileGate>} />
                </Route>
                <Route element={<PermissionRoute permission="rep.rp-rounds.manage" />}>
                  <Route path="/rep/drivers" element={<RepProfileGate><RepDriversPage /></RepProfileGate>} />
                </Route>
                {/* Admin overview of RP rounds + registry for back-office */}
                <Route element={<PermissionRoute permission="reference.view" />}>
                  <Route path="/admin/rp-rounds" element={<AdminRpRoundsPage />} />
                  <Route path="/clients" element={<ClientsPage />} />
                  <Route path="/regional-representatives" element={<RegionalRepresentativesPage />} />
                  <Route path="/cities" element={<CitiesPage />} />
                  <Route path="/drivers" element={<DriversPage />} />
                  <Route path="/transports" element={<TransportsPage />} />
                  <Route path="/branches" element={<BranchesPage />} />
                  <Route path="/route-directory" element={<RouteDirectoryPage />} />
                  <Route path="/tarifs" element={<TarifsPage />} />
                  <Route path="/additional-services" element={<AdditionalServicesPage />} />
                  <Route path="/round-extra-services" element={<RoundExtraServicesPage />} />
                  <Route path="/directions" element={<DirectionsPage />} />
                  <Route path="/delivery-times" element={<DeliveryTimesPage />} />
                  <Route path="/transport-types" element={<TransportTypesPage />} />
                  <Route path="/carriers" element={<CarriersPage />} />
                  <Route path="/box-types" element={<BoxTypesPage />} />
                  <Route path="/contact-faces" element={<ContactFacesPage />} />
                </Route>
                {/* Registry: back-office OR own RP portal */}
                <Route element={<PermissionRoute anyOf={['reference.view', 'rep.rounds.view']} />}>
                  <Route path="/rep-registry" element={<RepRegistryPage />} />
                </Route>

                <Route element={<PermissionRoute permission="reference.manage" />}>
                  <Route path="/couriers" element={<CouriersPage />} />
                  <Route path="/personals" element={<PersonalsPage />} />
                  <Route path="/resolutions" element={<ResolutionsPage />} />
                </Route>

                <Route path="/tariffs" element={<Navigate to="/tarifs" replace />} />
                <Route path="/orders" element={<Navigate to="/all-orders" replace />} />
                <Route path="/transit" element={<Navigate to="/all-orders" replace />} />
                <Route path="/orders/new" element={<NewOrderRedirect />} />
                <Route path="/orders/:id/edit" element={<RegionalOrderEditRedirect />} />
                <Route path="/rounds" element={<RoundsRedirect />} />
                <Route path="/city-rounds" element={<Navigate to="/all-rounds?type=city" replace />} />
                <Route path="/city-rounds/new" element={<CityRoundsNewRedirect />} />
                <Route path="/city-rounds/:id/edit" element={<CityRoundEditRedirect />} />
                <Route path="/city-orders" element={<Navigate to="/all-orders" replace />} />
                <Route path="/city-orders/new" element={<CityNewOrderRedirect />} />
                <Route path="/city-orders/:id/edit" element={<CityOrderEditRedirect />} />

                <Route element={<PermissionRoute anyOf={['users.view', 'users.manage']} />}>
                  <Route path="/users" element={<UsersPage />} />
                </Route>
                <Route element={<PermissionRoute permission="users.manage" />}>
                  <Route path="/roles" element={<RolesPage />} />
                  <Route path="/firms" element={<FirmsPage />} />
                  <Route path="/integrations" element={<IntegrationsPage />} />
                  <Route path="/audit-log" element={<AuditLogPage />} />
                </Route>
                <Route element={<PermissionRoute permission="users.manage" />}>
                  <Route path="/email-queue" element={<EmailQueuePage />} />
                </Route>
                <Route element={<PermissionRoute permission="feedback.view" />}>
                  <Route path="/tester-feedback" element={<TesterFeedbackPage />} />
                </Route>
                <Route element={<PermissionRoute anyOf={['orders.view', 'city-orders.view']} />}>
                  <Route path="/document-pack" element={<DocumentPackPage />} />
                </Route>
                {FEATURE_REPORTS_ENABLED ? (
                  <Route element={<PermissionRoute permission="finance.view" />}>
                    <Route path="/reports" element={<ReportsPage />} />
                    <Route path="/reports/summary" element={<Navigate to="/reports" replace />} />
                    <Route path="/reports/intermediate" element={<Navigate to="/reports" replace />} />
                    <Route path="/reports/rounds" element={<Navigate to="/reports" replace />} />
                    <Route path="/reports/services" element={<Navigate to="/reports" replace />} />
                    <Route path="/reports/costs" element={<Navigate to="/reports" replace />} />
                    <Route path="/reports/deadlines" element={<Navigate to="/reports" replace />} />
                    <Route path="/reports/months" element={<Navigate to="/reports" replace />} />
                    <Route path="/reports/return-cargo" element={<Navigate to="/reports" replace />} />
                    <Route path="/reports/receiving" element={<Navigate to="/reports" replace />} />
                    <Route path="/reports/margin" element={<MarginReportPage />} />
                  </Route>
                ) : (
                  <Route path="/reports/*" element={<Navigate to="/" replace />} />
                )}
                <Route element={<PermissionRoute permission="finance.view" />}>
                  <Route path="/finance" element={<FinancePage />} />
                  <Route path="/receivables" element={<ReceivablesPage />} />
                  <Route path="/delivery-registry" element={<DeliveryRegistryPage />} />
                  <Route path="/rep-settlements" element={<RepSettlementsPage />} />
                  <Route path="/margin-leaks" element={<MarginLeaksPage />} />
                </Route>
                <Route element={<PermissionRoute permission="reference.manage" />}>
                  <Route path="/banks" element={<BanksPage />} />
                  <Route path="/cost-types" element={<CostTypesPage />} />
                  <Route path="/exchange-rates" element={<ExchangeRatesPage />} />
                </Route>
                {/* banks/cost-types/exchange in nav require reference.manage */}
                <Route path="/receiving" element={<Navigate to="/orders?status=2" replace />} />
                <Route path="/messages" element={<MessagesPage />} />
                <Route element={<PermissionRoute anyOf={['orders.view', 'city-orders.view']} />}>
                  <Route path="/track" element={<TrackingPage />} />
                </Route>
                <Route path="/rates" element={<Navigate to="/" replace />} />
                <Route element={<PermissionRoute permission="rounds.scan" />}>
                  <Route path="/warehouse/portal" element={<Navigate to="/warehouse/city-rounds" replace />} />
                  <Route path="/warehouse/city-rounds" element={<WarehouseCityRoundsPage />} />
                  <Route path="/warehouse/regional-rounds" element={<WarehouseRegionalRoundsPage />} />
                  <Route path="/warehouse/rounds" element={<WarehouseRoundsPage />} />
                  <Route path="/warehouse/loading/:id" element={<WarehouseLoadingPage />} />
                  <Route path="/warehouse/receive" element={<WarehouseReceivePage />} />
                  <Route path="/warehouse/receive/:id" element={<WarehouseReceiveRoundPage />} />
                  <Route path="/warehouse/archive" element={<WarehouseArchivePage />} />
                  <Route path="/warehouse/search" element={<WarehouseSearchPage />} />
                  <Route path="/warehouse/journal" element={<WarehouseJournalPage />} />
                </Route>
                {FEATURE_WAREHOUSE_ENABLED ? (
                  <>
                    <Route element={<PermissionRoute permission="orders.view" />}>
                      <Route path="/acceptance-reports" element={<AcceptanceReportsPage />} />
                    </Route>
                    <Route element={<PermissionRoute anyOf={['orders.view', 'city-orders.view']} />}>
                      <Route path="/warehouse/stock" element={<WarehouseStockPage />} />
                    </Route>
                  </>
                ) : (
                  <>
                    <Route path="/acceptance-reports/*" element={<Navigate to="/" replace />} />
                    <Route path="/warehouse/*" element={<Navigate to="/" replace />} />
                  </>
                )}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
            </Route>
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
